import { defineStore } from "pinia";
import api from "../api";
import { useRoutesStore } from "./routes";
import { useMyRoutesStore } from "./myRoutes";

// Адрес маршрута - /route/<slug> (короткий случайный код, routes.slug), а не
// числовой id: id остаётся ключом API и БД, но в ссылки не попадает. Стор
// переводит код <-> id: сперва из загруженных списков (каталог, «Мои»), иначе
// запросом (GET /routes/by-slug/{slug} для кода; список/карточка - для id).
export const ROUTE_PATH_PREFIX = "/route/";
export const routePath = (slug: string) => `${ROUTE_PATH_PREFIX}${slug}`;
// Код из пути /route/<slug> (по пути, а не по имени роута - так не зависит от таблицы роутов).
export const slugFromPath = (path: string): string | null => {
  const m = /^\/route\/([^/?#]+)/.exec(path);
  return m ? decodeURIComponent(m[1]) : null;
};

type Item = { id: number; slug?: string | null };
let pending = new Map<string, Promise<number | null>>();

export const useRouteLinksStore = defineStore("routeLinks", {
  state: () => ({
    idBySlug: {} as Record<string, number>,
    slugById: {} as Record<number, string>,
    // Код, которого нет на сервере (или маршрут недоступен): не спрашиваем снова.
    unknown: {} as Record<string, true>,
  }),
  actions: {
    remember(id: number, slug: string | null | undefined) {
      if (!slug) return;
      this.idBySlug[slug] = id;
      this.slugById[id] = slug;
    },
    _scan(items: Item[]) {
      for (const it of items) this.remember(it.id, it.slug);
    },
    // Из уже загруженных списков, без запросов.
    slugOf(id: number): string | null {
      this._scan(useRoutesStore().items);
      this._scan(useMyRoutesStore().items);
      return this.slugById[id] ?? null;
    },
    idOf(slug: string): number | null {
      const known = this.idBySlug[slug];
      if (known != null) return known;
      this._scan(useRoutesStore().items);
      this._scan(useMyRoutesStore().items);
      return this.idBySlug[slug] ?? null;
    },
    async ensureSlug(id: number): Promise<string | null> {
      const cached = this.slugOf(id);
      if (cached) return cached;
      await useRoutesStore().fetch();
      const afterList = this.slugOf(id);
      if (afterList) return afterList;
      try {
        const res = await api.get<Item>(`/routes/${id}`);
        this.remember(res.data.id, res.data.slug);
      } catch {
        // маршрут недоступен - ссылка останется со старым ?route=<id>
      }
      return this.slugById[id] ?? null;
    },
    async resolveSlug(slug: string): Promise<number | null> {
      const known = this.idOf(slug);
      if (known != null) return known;
      if (this.unknown[slug]) return null;
      let p = pending.get(slug);
      if (!p) {
        p = (async () => {
          try {
            const res = await api.get<Item>(`/routes/by-slug/${encodeURIComponent(slug)}`);
            this.remember(res.data.id, res.data.slug ?? slug);
            return res.data.id;
          } catch {
            this.unknown[slug] = true;
            return null;
          } finally {
            pending.delete(slug);
          }
        })();
        pending.set(slug, p);
      }
      return p;
    },
    // Сброс после входа/выхода: приватный маршрут мог стать (не)доступен.
    forgetUnknown() {
      this.unknown = {};
    },
  },
});
